import { useState, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { Download } from "lucide-react";
import { EXPORT_ORGANIZATION_ORDERS } from "../graphql";
import { exportToCsv, buildExportFilename } from "../../roster/utils/exportCsv";
import type { CsvColumn } from "../../roster/utils/exportCsv";
import { koinsToEur, CATEGORY_LABEL_KEYS } from "../constants";
import type { MarketplaceOrder, MarketplaceCategory, OrderStatus, MarketplaceOrdersResult } from "../types";
import { useLocale } from "../../../context/LanguageContext";

/** Backend clamps organizationOrders limit to 200. */
const BATCH_SIZE = 200;
/** Safety cap to prevent runaway loops. */
const MAX_ROWS = 10_000;

interface ExportOrdersButtonProps {
  organizationId: string;
  orgSlug: string;
  /** Current status filter — export respects the active filter */
  statusFilter: OrderStatus | undefined;
}

const STATUS_LABEL_KEYS: Record<OrderStatus, string> = {
  PENDING: "orders.statusPending",
  PROCESSING: "orders.statusProcessing",
  FULFILLED: "orders.statusFulfilled",
  CANCELLED: "orders.statusCancelled",
  REFUNDED: "orders.statusRefunded",
};

function buildOrdersColumns(
  t: (key: string, params?: Record<string, string | number>) => string,
): CsvColumn<MarketplaceOrder>[] {
  return [
    {
      header: t("orders.export.colOrderDate"),
      accessor: (o) => o.createdAt ? o.createdAt.slice(0, 10) : "",
    },
    {
      header: t("orders.export.colMemberName"),
      accessor: (o) =>
        o.user
          ? `${o.user.firstName ?? ""} ${o.user.lastName ?? ""}`.trim() || t("common.unknown")
          : t("common.unknown"),
    },
    { header: t("orders.export.colMemberEmail"), accessor: (o) => o.user?.email ?? "" },
    { header: t("orders.export.colRewardName"), accessor: (o) => o.item?.name ?? t("common.unknown") },
    {
      header: t("orders.export.colCategory"),
      accessor: (o) =>
        t(CATEGORY_LABEL_KEYS[(o.item?.category as MarketplaceCategory) ?? "OTHER"] ?? CATEGORY_LABEL_KEYS.OTHER),
    },
    { header: t("orders.export.colKoinsSpent"), accessor: (o) => o.koinsSpent },
    { header: t("orders.export.colEurValue"), accessor: (o) => koinsToEur(o.koinsSpent) },
    {
      header: t("orders.export.colStatus"),
      accessor: (o) => (STATUS_LABEL_KEYS[o.status] ? t(STATUS_LABEL_KEYS[o.status]) : o.status),
    },
    {
      header: t("orders.export.colFulfilledAt"),
      accessor: (o) => o.fulfilledAt ? o.fulfilledAt.slice(0, 10) : "",
    },
    {
      header: t("orders.export.colCancelledAt"),
      accessor: (o) => o.cancelledAt ? o.cancelledAt.slice(0, 10) : "",
    },
    { header: t("orders.export.colCancellationReason"), accessor: (o) => o.cancellationReason ?? "" },
    { header: t("orders.export.colFulfillmentDetails"), accessor: (o) => o.fulfillmentDetails ?? "" },
  ];
}

export function ExportOrdersButton({
  organizationId,
  orgSlug,
  statusFilter,
}: ExportOrdersButtonProps) {
  const [exporting, setExporting] = useState(false);
  const client = useApolloClient();
  const { t } = useLocale();

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const allOrders: MarketplaceOrder[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore && allOrders.length < MAX_ROWS) {
        const { data } = await client.query<{ organizationOrders: MarketplaceOrdersResult }>({
          query: EXPORT_ORGANIZATION_ORDERS,
          variables: {
            input: {
              organizationId,
              limit: BATCH_SIZE,
              offset,
              ...(statusFilter ? { status: statusFilter } : {}),
            },
          },
          fetchPolicy: "network-only",
        });

        const page = data?.organizationOrders;
        if (!page?.orders?.length) break;

        allOrders.push(...page.orders);
        offset += BATCH_SIZE;
        hasMore = page.orders.length === BATCH_SIZE;
      }

      if (allOrders.length === 0) {
        console.warn("[ExportOrdersButton] No data to export");
        return;
      }

      exportToCsv({
        filename: buildExportFilename(orgSlug, "orders"),
        columns: buildOrdersColumns(t),
        rows: allOrders,
      });
    } catch (err) {
      console.error("[ExportOrdersButton] Export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [client, organizationId, orgSlug, statusFilter, t]);

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={exporting}
      className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
    >
      {exporting ? (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      ) : (
        <Download className="h-3.5 w-3.5" />
      )}
      {t("common.exportCsv")}
    </button>
  );
}
